import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import {
  Chip,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMemo } from 'react';
import { formatDate } from '@/shared/lib';
import { Switch } from '@/shared/ui';
import { getOptionColors } from './getOptionColors';
import { resolveSplitOrder } from './resolveSplitOrder';
import type { ListProps } from './types';

const identity = (option: string) => option;

const OPEN_COHORT_TOOLTIP_LEAVE_DELAY = 300;

const TURN_OFF_LABEL = 'Turn off “Show only full cohorts”';

const List = styled('ul')(() => ({
  margin: 0,
  padding: 0,
  listStyleType: 'none',
  overflow: 'auto',
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  flexDirection: 'column',
}));
const Item = styled('li')(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  backgroundColor: 'white',
  lineHeight: 1.5,
  fontSize: '0.75rem',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),

  '&:hover': {
    backgroundColor: 'rgba(6, 63, 254, 0.12)', // --accent-700
  },
}));
const OptionName = styled('span')(() => ({
  flexGrow: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

function OpenCohortNotice({ onTurnOff }: { onTurnOff?: () => void }) {
  return (
    <>
      Cohort is still open, so it’s hidden from the chart.{' '}
      {onTurnOff ? (
        <Link
          component="button"
          type="button"
          color="inherit"
          onClick={onTurnOff}
        >
          {TURN_OFF_LABEL}
        </Link>
      ) : (
        TURN_OFF_LABEL
      )}{' '}
      to see its data.
    </>
  );
}

export function SplitLegendList({
  isDateContent,
  filterQuery,
  options,
  palette,
  optionColors,
  sort,
  isChronologicalSplit,
  valueByOption,
  openOptionsSet,
  onOptionsToggle,
  onOptionHover,
  onShowOnlyFullCohortsChange,
  inactiveOptionsSet,
}: ListProps) {
  // Indexed into the UNSORTED options, as the chart assigns line colours.
  const fallbackColors = useMemo(() => {
    return getOptionColors(options, palette);
  }, [options, palette]);
  const preparedOptions = useMemo(() => {
    const preparedQuery = filterQuery.toLowerCase();
    const filtered = options.filter((option) => {
      return option.toLowerCase().includes(preparedQuery);
    });

    return resolveSplitOrder({
      options: filtered,
      getKey: identity,
      getLabel: identity,
      sort,
      isChronologicalSplit: !!isChronologicalSplit,
      valueByOption,
    });
  }, [options, filterQuery, sort, isChronologicalSplit, valueByOption]);
  const selectableOptions = useMemo(() => {
    return preparedOptions.filter((option) => !openOptionsSet?.has(option));
  }, [preparedOptions, openOptionsSet]);
  const allOptionsChecked = useMemo(() => {
    return (
      selectableOptions.length > 0 &&
      selectableOptions.every((option) => !inactiveOptionsSet.has(option))
    );
  }, [selectableOptions, inactiveOptionsSet]);

  if (preparedOptions.length === 0) {
    return (
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{ my: 2, textAlign: 'center' }}
      >
        Nothing found.
      </Typography>
    );
  }

  const handleOptionToggle =
    (option: string) =>
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onOptionsToggle([option], checked);
    };
  const handleAllOptionsToggle = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    onOptionsToggle(selectableOptions, checked);
  };
  const handleTurnOff = onShowOnlyFullCohortsChange
    ? () => onShowOnlyFullCohortsChange(false)
    : undefined;

  return (
    <List>
      <Item sx={{ '&:hover': { backgroundColor: 'inherit' } }}>
        <Switch
          checked={allOptionsChecked}
          disabled={selectableOptions.length === 0}
          onChange={handleAllOptionsToggle}
        />
        All
      </Item>
      <Divider />
      {preparedOptions.map((option) => {
        let name = option;

        if (isDateContent) {
          name = formatDate(name);
        }

        const isOpen = !!openOptionsSet?.has(option);

        return (
          <Item
            key={option}
            onMouseEnter={() => onOptionHover?.(option)}
            onMouseLeave={() => onOptionHover?.(null)}
          >
            <Switch
              customColor={
                optionColors?.get(option) ?? fallbackColors.get(option)
              }
              checked={!isOpen && !inactiveOptionsSet.has(option)}
              disabled={isOpen}
              onChange={handleOptionToggle(option)}
            />
            <Tooltip title={name}>
              <OptionName>{name}</OptionName>
            </Tooltip>
            {isOpen && (
              <>
                <Chip
                  variant="outlined"
                  size="small"
                  icon={<ScheduleOutlinedIcon />}
                  label="Not closed"
                  sx={{ flexShrink: 0 }}
                />
                <Tooltip
                  arrow
                  leaveDelay={OPEN_COHORT_TOOLTIP_LEAVE_DELAY}
                  title={<OpenCohortNotice onTurnOff={handleTurnOff} />}
                >
                  <IconButton
                    size="small"
                    disableRipple
                    aria-label={`Why ${name} is hidden`}
                    sx={{ p: 0, flexShrink: 0, color: 'error.main' }}
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Item>
        );
      })}
    </List>
  );
}
